import { z } from 'zod';
import {
  ALL_PERMISSIONS,
  MODULE_PERMISSIONS,
  isTenantAdminOnly,
  type Permission,
} from './permissions';
import { MODULE_CATALOGUE, modulePermission } from './modules';
import {
  HOST_ADMIN_RANK,
  MAX_SCHOOL_RANK,
  MIN_SCHOOL_RANK,
  type RoleCategoryId,
} from './role-categories';

export const SEEDED_ROLE_IDS = ['tenant_admin', 'local_admin', 'slt', 'teacher', 'user'] as const;
export type SeededRoleId = (typeof SEEDED_ROLE_IDS)[number];

export const MIN_RANK = MIN_SCHOOL_RANK;
/** Highest rank inside a school (decision 2026-10-10 #4: 100 is the reserved Host Admin ceiling). */
export const MAX_RANK = MAX_SCHOOL_RANK;
export const TENANT_ADMIN_RANK = MAX_SCHOOL_RANK;

export const roleIdSchema = z.string().regex(/^[a-z][a-z0-9_]{1,39}$/);
/** Rank a client may set: 1–99. */
export const rankSchema = z.number().int().min(MIN_RANK).max(MAX_RANK);
/**
 * Rank as stored and returned: 1–100. Only a role kept from before role categories can still
 * sit at 100; it is flagged in the roles report and nobody in the school can assign it.
 */
export const storedRankSchema = z.number().int().min(MIN_RANK).max(HOST_ADMIN_RANK);

export interface RoleDefinition {
  id: string;
  name: string;
  rank: number;
  category: RoleCategoryId;
  permissions: readonly Permission[];
  seeded: boolean;
}

const teacherModules = MODULE_CATALOGUE.filter((m) => m.teacher).map(
  (m) => modulePermission(m.id) as Permission,
);
const readOnlyModules = MODULE_CATALOGUE.filter((m) => m.readOnlySafe).map(
  (m) => modulePermission(m.id) as Permission,
);

/**
 * SLT (senior leadership, decision 2026-10-10 #5): see and invite staff, assign roles ranked
 * below 60, read the audit log, and use every module. No role editing and nothing school-wide.
 */
export const SLT_PERMISSIONS: readonly Permission[] = [
  'users.read',
  'users.invite',
  'users.assign_roles',
  'audit.read',
  ...MODULE_PERMISSIONS,
];

/** Seeded default roles (brief §6.2). Host Admin lives in the central realm, not here. */
export const SEEDED_ROLES: readonly RoleDefinition[] = [
  {
    id: 'tenant_admin',
    name: 'Tenant Admin',
    rank: TENANT_ADMIN_RANK,
    category: 'tenant_admin',
    permissions: ALL_PERMISSIONS,
    seeded: true,
  },
  {
    id: 'local_admin',
    name: 'Local Admin',
    rank: 80,
    category: 'local_admin',
    permissions: [
      'users.read',
      'users.invite',
      'users.assign_roles',
      'roles.edit',
      'audit.read',
      ...MODULE_PERMISSIONS,
    ],
    seeded: true,
  },
  {
    id: 'slt',
    name: 'SLT',
    rank: 60,
    category: 'slt',
    permissions: SLT_PERMISSIONS,
    seeded: true,
  },
  {
    id: 'teacher',
    name: 'Teacher',
    rank: 50,
    category: 'teachers',
    permissions: teacherModules,
    seeded: true,
  },
  {
    id: 'user',
    name: 'User',
    rank: 10,
    category: 'users',
    permissions: readOnlyModules,
    seeded: true,
  },
];

/** Union of permissions across all held roles (brief §6.2). */
export function effectivePermissions(
  roles: readonly Pick<RoleDefinition, 'permissions'>[],
): Set<Permission> {
  return new Set(roles.flatMap((r) => r.permissions));
}

/**
 * The highest rank among the roles, capped at the school maximum (99): a role kept at the
 * reserved 100 from before role categories never lifts anyone above the Tenant Admin.
 */
export function highestRank(roles: readonly Pick<RoleDefinition, 'rank'>[]): number {
  return Math.min(
    MAX_RANK,
    roles.reduce((max, r) => Math.max(max, r.rank), 0),
  );
}

/** Escalation guard, part 1: actor may only assign roles ranked strictly below their own highest. */
export function canAssignRole(
  actorRoles: readonly Pick<RoleDefinition, 'rank'>[],
  target: Pick<RoleDefinition, 'rank'>,
): boolean {
  return target.rank < highestRank(actorRoles);
}

/** Escalation guard, part 2: actor may only grant permissions they hold. Returns the ones they can't grant. */
export function ungrantablePermissions(
  actorRoles: readonly Pick<RoleDefinition, 'permissions'>[],
  requested: readonly Permission[],
): Permission[] {
  const held = effectivePermissions(actorRoles);
  return requested.filter((p) => !held.has(p));
}

/**
 * tenant_admin always keeps every permission, so a school can't lock itself out. Every other
 * role is stripped of tenant-admin-only permissions (defence in depth; servers reject first).
 */
export function normaliseRolePermissions(
  roleId: string,
  permissions: readonly Permission[],
): Permission[] {
  if (roleId === 'tenant_admin') return [...ALL_PERMISSIONS];
  return [...new Set(permissions)].filter((p) => !isTenantAdminOnly(p));
}

/** Tenant-admin-only permissions requested for a role that isn't tenant_admin (→ 403 TENANT_ADMIN_ONLY). */
export function tenantAdminOnlyViolations(roleId: string, requested: readonly string[]): string[] {
  return roleId === 'tenant_admin' ? [] : requested.filter(isTenantAdminOnly);
}

/**
 * Who may edit or delete a role (name, rank, permissions): holders of roles.edit, only for roles
 * ranked strictly below their own highest, never tenant_admin. A new rank (when given) must also
 * stay below theirs. A custom role kept at the reserved 100 (before role categories) can only
 * be fixed by the school's top rank, the Tenant Admin, and only by moving it below 99.
 */
export function canEditRole(
  actorRoles: readonly Pick<RoleDefinition, 'rank'>[],
  target: Pick<RoleDefinition, 'id' | 'rank'>,
  newRank?: number,
): boolean {
  const ceiling = highestRank(actorRoles);
  if (target.id === 'tenant_admin') return false;
  if (newRank !== undefined && newRank >= ceiling) return false;
  return target.rank < ceiling || (target.rank > MAX_RANK && ceiling === MAX_RANK);
}

export function isSeededRole(roleId: string): roleId is SeededRoleId {
  return (SEEDED_ROLE_IDS as readonly string[]).includes(roleId);
}
